/**
 * This is the base for the main app, once you are logged in.
 */

import { ApolloProvider } from '@apollo/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';

import { AlertProvider } from '~/components/LegacyAlert/LegacyAlert';
import { Bouncer } from '~/components/Bouncer/Bouncer';
import { Navigation } from '~/components/Navigation/Navigation';
import { Routes } from '~/components/Routes/Routes';
import { Login } from '~/components/Login/Login';

import { AppContextProvider } from './AppContext';
import { getClient } from '~/apollo/client';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { PageError } from '~/components/PageError';

import './App.scss';
import { SidebarPageTemplate } from '~/components/SidebarPageTemplate/SidebarPageTemplate';
import { Loading } from '~/components/Loading/Loading';
import { featureFlags } from '~/featureFlags';
import { FeatureFlagIds } from '~/util/FeatureFlagsRepository';
import { EchoChamberContextProvider, GlobalEchoChamber } from '~/EchoChamberContext';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';
import { installClipsTest } from '~/services/clips';
import { ClipsContextProvider } from '~/context/ClipsContext';

// This is temporary code to help test the clips service integration.
installClipsTest();

// This makes the feature flag controls available in the console for l337 h4x0r st4k3h0ld3rz
( ( window || globalThis || global ) as any ).ff = {
  flags: featureFlags.getObject(),
  list: () => featureFlags.list(),
  on: ( key: FeatureFlagIds<typeof featureFlags> ) => featureFlags.enable( key ),
  off: ( key: FeatureFlagIds<typeof featureFlags> ) => featureFlags.disable( key ),
  reset: ( key: FeatureFlagIds<typeof featureFlags> ) => featureFlags.reset( key ),
  resetAll: () => featureFlags.resetAll(),
};

const LoadingView = () => <SidebarPageTemplate hideSidebar main={ <Loading graphicOnly /> } />;
const ErrorView = ( { error }: { error: any } ) => <PageError error={ error } standalone />;

const queryClient = new QueryClient();

const opsPortal = (
  <BrowserRouter>
    <QueryParamProvider adapter={ ReactRouter5Adapter } options={ { removeDefaultsFromUrl: true } }>
      <ErrorBoundary logHeading="🔥 A page level error has occurred 🔥" ErrorView={ ErrorView }>
        <ApolloProvider client={ getClient() }>
          <QueryClientProvider client={ queryClient }>
            <Bouncer LoginView={ Login }>
              <AppContextProvider>
                <ClipsContextProvider>
                  <AlertProvider>
                    <EchoChamberContextProvider>
                      <Navigation />
                      <div id="main" className="container-fluid">
                        <Suspense fallback={ <LoadingView /> }>
                          <Routes />
                        </Suspense>
                      </div>
                      <GlobalEchoChamber />
                    </EchoChamberContextProvider>
                  </AlertProvider>
                </ClipsContextProvider>
              </AppContextProvider>
            </Bouncer>
          </QueryClientProvider>
        </ApolloProvider>
      </ErrorBoundary>
    </QueryParamProvider>
  </BrowserRouter>
);

const root = document.getElementById( 'root' );

ReactDOM.render( opsPortal, root );
